import { useState, useEffect, useCallback, useRef } from "react";

import { cn } from "@/lib/utils";
import type { Theme } from "@/types/theme.types";
import {
  FileMentionItem,
  ProjectMentionItem,
  ThemeMentionItem,
} from "./mention-items";

type MentionTab = "all" | "files" | "projects" | "themes";

export type MentionType = "file" | "project" | "theme" | "skill";

export interface MentionSelection {
  type: MentionType;
  name: string;
  id?: string;
  data?: Record<string, unknown>;
}

interface ReferenceableProject {
  id: string;
  name: string;
  description: string | null;
  mode: string;
  devContainerStatus: string | null;
}

interface MentionDropdownProps {
  isOpen: boolean;
  query: string;
  selectedIndex: number;
  onIndexChange: (index: number) => void;
  onSelect: (selection: MentionSelection) => void;
  /** Called by parent on Enter/Tab key — selects item at selectedIndex */
  confirmSelectionRef?: React.MutableRefObject<(() => void) | null>;
  /** Called by parent on Left/Right arrow — switches tab */
  switchTabRef?: React.MutableRefObject<((direction: 'left' | 'right') => void) | null>;
  /** Exposes current item count so parent can clamp selectedIndex */
  itemCountRef?: React.MutableRefObject<number>;
  // Data sources
  files: string[];
  projects: ReferenceableProject[];
  themes: Theme[];
  currentTheme: Theme | null;
  isLoadingProjects: boolean;
}

/** Flatten all items across tabs for unified index tracking */
interface FlatItem {
  type: MentionType;
  name: string;
  id?: string;
  description?: string | null;
  status?: string | null;
}

export function MentionDropdown({
  isOpen,
  query,
  selectedIndex,
  onIndexChange,
  onSelect,
  confirmSelectionRef,
  switchTabRef,
  itemCountRef,
  files,
  projects,
  themes,
  currentTheme,
  isLoadingProjects,
}: MentionDropdownProps) {
  const [activeTab, setActiveTab] = useState<MentionTab>("all");
  const listRef = useRef<HTMLDivElement>(null);

  // Reset tab when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab("all");
    }
  }, [isOpen]);

  const lowerQuery = query.toLowerCase();

  // Filter items by query (no artificial limits — scrollable list handles overflow)
  const filteredFiles = files.filter((f) =>
    f.toLowerCase().includes(lowerQuery),
  );

  const filteredProjects = projects.filter((p) =>
    p.name.toLowerCase().includes(lowerQuery),
  );

  const filteredThemes = themes.filter((t) =>
    t.name.toLowerCase().includes(lowerQuery),
  );

  // Build flat item list based on active tab
  // All tab order: Projects → Files → Themes
  const getItems = (): FlatItem[] => {
    const items: FlatItem[] = [];

    if (activeTab === "all" || activeTab === "projects") {
      for (const p of filteredProjects) {
        items.push({
          type: "project",
          name: p.name,
          id: p.id,
          description: p.description,
          status: p.devContainerStatus,
        });
      }
    }

    if (activeTab === "all" || activeTab === "files") {
      for (const f of filteredFiles) {
        items.push({ type: "file", name: f });
      }
    }

    if (activeTab === "all" || activeTab === "themes") {
      // Add current theme first
      if (
        currentTheme &&
        currentTheme.name.toLowerCase().includes(lowerQuery)
      ) {
        items.push({ type: "theme", name: currentTheme.name });
      }
      for (const t of filteredThemes) {
        if (t.themeId !== currentTheme?.themeId) {
          items.push({ type: "theme", name: t.name });
        }
      }
    }

    return items;
  };

  const items = getItems();

  const handleSelect = (item: FlatItem) => {
    onSelect({
      type: item.type,
      name: item.name,
      id: item.id,
      data:
        item.type === "project"
          ? { description: item.description, status: item.status }
          : undefined,
    });
  };

  const tabs: { key: MentionTab; label: string }[] = [
    { key: "all", label: "All" },
    { key: "projects", label: "Projects" },
    { key: "files", label: "Files" },
    { key: "themes", label: "Themes" },
  ];

  // Auto-scroll selected item into view
  useEffect(() => {
    if (!listRef.current || !isOpen) return;
    const el = listRef.current.querySelector(`[data-mention-index="${selectedIndex}"]`);
    if (el) {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex, isOpen]);

  // Expose item count for parent to clamp selectedIndex
  useEffect(() => {
    if (itemCountRef) {
      itemCountRef.current = items.length;
    }
  });

  // Expose confirmSelection for parent to call on Enter/Tab
  useEffect(() => {
    if (confirmSelectionRef) {
      confirmSelectionRef.current = () => {
        const clamped = Math.min(selectedIndex, items.length - 1);
        if (items[clamped]) {
          handleSelect(items[clamped]);
        }
      };
    }
    if (switchTabRef) {
      switchTabRef.current = (direction: 'left' | 'right') => {
        const currentIdx = tabs.findIndex((t) => t.key === activeTab);
        const nextIdx = direction === 'right'
          ? (currentIdx + 1) % tabs.length
          : (currentIdx - 1 + tabs.length) % tabs.length;
        setActiveTab(tabs[nextIdx].key);
        onIndexChange(0);
      };
    }
  });

  if (!isOpen) return null;

  return (
    <div className="absolute bottom-full left-0 mb-1 bg-popover border border-border rounded-lg shadow-lg overflow-hidden z-50 w-80 max-h-[400px] flex flex-col">
      {/* Tab bar */}
      <div className="flex border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={cn(
              "px-3 py-1.5 text-xs font-medium transition-colors",
              activeTab === tab.key
                ? "text-primary border-b-2 border-primary"
                : "text-muted-foreground hover:text-foreground",
            )}
            onClick={() => {
              setActiveTab(tab.key);
              onIndexChange(0);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Scrollable items list */}
      <div ref={listRef} className="overflow-y-auto flex-1">
        {isLoadingProjects && activeTab !== "files" && (
          <div className="px-3 py-4 text-sm text-muted-foreground text-center">
            Loading...
          </div>
        )}

        {items.length === 0 && !isLoadingProjects && (
          <div className="px-3 py-4 text-sm text-muted-foreground text-center">
            No results found
          </div>
        )}

        {items.map((item, index) => {
          if (item.type === "file") {
            return (
              <div key={`file-${item.name}`} data-mention-index={index}>
                <FileMentionItem
                  name={item.name}
                  isSelected={index === selectedIndex}
                  onSelect={() => handleSelect(item)}
                  onHover={() => onIndexChange(index)}
                />
              </div>
            );
          }
          if (item.type === "project") {
            return (
              <div key={`project-${item.id}`} data-mention-index={index}>
                <ProjectMentionItem
                  name={item.name}
                  description={item.description}
                  status={item.status}
                  isSelected={index === selectedIndex}
                  onSelect={() => handleSelect(item)}
                  onHover={() => onIndexChange(index)}
                />
              </div>
            );
          }
          return (
            <div key={`theme-${item.name}`} data-mention-index={index}>
              <ThemeMentionItem
                name={item.name}
                isSelected={index === selectedIndex}
                onSelect={() => handleSelect(item)}
                onHover={() => onIndexChange(index)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
